import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Bookmark, Trash2, Search, Loader2, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedSearchesPage() {
  const { tr } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/classifieds/saved-searches`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSearch = (f) => {
    const p = new URLSearchParams();
    if (f.category) p.set('category', f.category);
    if (f.subcategory) p.set('subcat', f.subcategory);
    if (f.region) p.set('region', f.region);
    if (f.search) p.set('search', f.search);
    if (f.price_min != null) p.set('price_min', String(f.price_min));
    if (f.price_max != null) p.set('price_max', String(f.price_max));
    navigate(`/sludinajumi?${p.toString()}`);
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await axios.delete(`${API}/classifieds/saved-searches/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success(tr('Dzēsts', 'Deleted', 'Удалено'));
    } catch (e) { toast.error(tr('Kļūda', 'Error', 'Ошибка')); }
    setBusy('');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="saved-searches-page">
      <div className="max-w-2xl mx-auto px-5 py-8">
        <button onClick={() => navigate('/sludinajumi')} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Uz sludinājumiem', 'To listings', 'К объявлениям')}
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-[#E6F2F0] flex items-center justify-center"><Bookmark className="w-6 h-6 text-[#2F7E79]" /></div>
          <h1 className="font-outfit text-3xl font-bold text-[#121212]">{tr('Saglabātie meklējumi', 'Saved searches', 'Сохранённые поиски')}</h1>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-6 flex items-center gap-1.5">
          <BellRing className="w-4 h-4 text-[#2F7E79]" />
          {tr('Saņem paziņojumu lietotnē, tiklīdz parādās jauns atbilstošs sludinājums.', 'Get an in-app notification as soon as a new matching listing appears.', 'Получайте уведомление в приложении, как только появится новое подходящее объявление.')}
        </p>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2F7E79]" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center bg-white border border-[#E5E4E0] rounded-2xl" data-testid="saved-searches-empty">
            <div className="text-4xl mb-3">🔖</div>
            <p className="text-[#5C5C5C] mb-4">{tr('Vēl nav saglabātu meklējumu.', 'No saved searches yet.', 'Пока нет сохранённых поисков.')}</p>
            <Button onClick={() => navigate('/sludinajumi')} className="bg-[#2F7E79] hover:bg-[#276b66] text-white rounded-full gap-2" data-testid="browse-listings-btn">
              <Search className="w-4 h-4" /> {tr('Pārlūkot sludinājumus', 'Browse listings', 'Смотреть объявления')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="saved-searches-list">
            {items.map((s) => (
              <div key={s.id} className="bg-white border border-[#E5E4E0] rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`saved-search-${s.id}`}>
                <button onClick={() => openSearch(s.filters || {})} className="flex items-center gap-3 text-left flex-1 min-w-0 group" data-testid={`open-saved-search-${s.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#E6F2F0] flex items-center justify-center flex-shrink-0">
                    <Search className="w-5 h-5 text-[#2F7E79]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#121212] truncate group-hover:text-[#2F7E79] transition-colors">{s.label}</p>
                    <p className="text-xs text-[#8A8A8A]">{tr('Skatīt rezultātus', 'View results', 'Посмотреть результаты')}</p>
                  </div>
                </button>
                <button onClick={() => remove(s.id)} disabled={busy === s.id}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors flex-shrink-0" data-testid={`delete-saved-search-${s.id}`}>
                  {busy === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
